import type { ReactNode } from "react";
import { Outlet, createFileRoute } from "@tanstack/react-router";

import { ConsoleShell, ConsoleSignOutButton } from "~/components/console/ConsoleShell";
import { StaffSignIn } from "~/components/StaffSignIn";
import { loadStaffMessages, useI18n } from "~/lib/i18n";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/console")({
  loader: () => loadStaffMessages(),
  component: ConsoleLayout,
});

/** Gates every console page behind a regulator staff session. */
function ConsoleLayout() {
  const { t } = useI18n();
  const staff = useStaffSession();
  const session = staff.session;

  if (!session) {
    let body: ReactNode;
    if (staff.isPending) {
      body = <p className="text-slate-600 dark:text-slate-400">{t("loading")}</p>;
    } else if (staff.isError) {
      body = <p className="text-brand dark:text-brand-dark">{t("somethingWrong")}</p>;
    } else {
      body = (
        <>
          <p className="text-slate-600 dark:text-slate-400">{t("consoleSignInBody")}</p>
          <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
            <StaffSignIn showRole={can.useConsole} />
          </div>
        </>
      );
    }

    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className={ui.h1}>{t("consoleTitle")}</h1>
        {body}
      </div>
    );
  }

  if (!can.useConsole(session.role)) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className={ui.h1}>{t("consoleTitle")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("consoleNotRegulator")}</p>
        <ConsoleSignOutButton />
      </div>
    );
  }

  return (
    <ConsoleShell session={session}>
      <Outlet />
    </ConsoleShell>
  );
}
